// Camera MediaStream singleton.
// getUserMedia must be invoked inside a user gesture. Capture phase re-uses
// the same stream — releasing it on app teardown only.
//
// Constraint strategy: ask for the widest FoV the device can provide.
// - Don't pin width/height "ideal" to a specific resolution — many phones
//   (Samsung S-series in particular) will respond by serving a tight crop
//   off the sensor, which feels "zoomed in" and makes it hard to fit a full
//   face in the viewfinder.
// - Asking for a small ideal width nudges the browser toward a wider sensor
//   read-out (the same logical area, fewer pixels, more of the scene).
// - After the stream is live, we try to push the track's `zoom` capability
//   to its minimum — this often equals the camera's true wide FoV on
//   devices that expose digital zoom.

let stream: MediaStream | null = null;

export async function requestCamera(): Promise<MediaStream> {
  if (stream) return stream;
  // No width/height constraints — request the device's natural stream so we
  // never accidentally trigger a cropped read-out. The CSS layer fits whatever
  // resolution the camera returns into the 9:16 frame.
  stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode: "user" },
    audio: false,
  });

  // Wide-out: if the track exposes a `zoom` capability, clamp to its minimum.
  // Best-effort — silently skipped where unsupported (most desktops, iOS).
  const track = stream.getVideoTracks()[0];
  if (track) {
    try {
      const caps = (track.getCapabilities?.() ?? {}) as MediaTrackCapabilities & {
        zoom?: { min: number; max: number; step: number };
      };
      if (caps.zoom && typeof caps.zoom.min === "number") {
        await track.applyConstraints({
          advanced: [{ zoom: caps.zoom.min } as MediaTrackConstraintSet],
        });
      }
    } catch (err) {
      console.debug("[camera] zoom-out best-effort failed", err);
    }
  }

  // Diagnostic: log the device list (post-permission, so labels are populated)
  // and the live track's settings + capabilities. Lets us see, after a phone
  // session, whether a wider front-camera lens is exposed under a separate
  // deviceId, and what zoom/width range is actually available.
  if (track) {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const fronts = devices
        .filter((d) => d.kind === "videoinput")
        .map((d) => ({ label: d.label, deviceId: d.deviceId.slice(0, 8) + "…" }));
      console.info("[camera] videoinput devices:", fronts);
      console.info("[camera] active track settings:", track.getSettings?.());
      console.info("[camera] active track caps:", track.getCapabilities?.());
    } catch (err) {
      console.debug("[camera] diagnostic enumerate failed", err);
    }
  }

  return stream;
}

export function getCamera(): MediaStream | null {
  return stream;
}

export function releaseCamera(): void {
  if (!stream) return;
  for (const track of stream.getTracks()) track.stop();
  stream = null;
}
